import React from 'react';
import { useSelector } from 'react-redux';
import { Carousel, CarouselItem, Button } from '@americanexpress/dls-react';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import { useCenturionNetworkCarouselImages } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { useAnalyticsContext } from '@/context/analyticsContext';
import { ExtendReactlytics } from '@americanexpress/reactlytics';
import styles from './CenturionCarrouselSection.module.scss';

type CenturionCarouselImage = {
  image: string;
  imageAlt?: string;
  mobileImage?: string;
  mobileImageAlt?: string;
  cta?: string;
  url?: string;
};

const fallbackCarouselImages: CenturionCarouselImage[] = [
  {
    image: '1-SLC-Lounge-Image-desktop.png',
    imageAlt: 'The Centurion® Lounge, Salt Lake City',
    mobileImage: '1-SLC-Lounge-Image-mobile.png',
    mobileImageAlt: 'The Centurion® Lounge, Salt Lake City Mobile',
    cta: 'Explore Lounge',
    url: 'https://www.thecenturionlounge.com/locations/slc/',
  },
  {
    image: '2-JPN-Lounge-Image-desktop.png',
    imageAlt: 'The Centurion® Lounge, Tokyo',
    mobileImage: '2-JPN-Lounge-Image-mobile.png',
    mobileImageAlt: 'The Centurion® Lounge, Tokyo Mobile',
    cta: 'Explore Lounge',
    url: 'https://www.thecenturionlounge.com/locations/hnd/',
  },
  {
    image: '3-ATL-Lounge-Image-desktop.png',
    imageAlt: 'The Centurion® Lounge, Atlanta',
    mobileImage: '3-ATL-Lounge-Image-mobile.png',
    mobileImageAlt: 'The Centurion® Lounge, Atlanta Mobile',
    cta: 'Explore Lounge',
    url: 'https://www.thecenturionlounge.com/locations/atl/',
  },
  {
    image: 'sidecar-LAS.jpg',
    imageAlt: 'The Centurion® Lounge, Las Vegas',
    mobileImage: 'Lounge-Carousel-LAS-Sidecar-mobile.png',
    mobileImageAlt: 'The Centurion® Lounge, Las Vegas Mobile',
    cta: 'Explore Lounge',
    url: 'https://www.thecenturionlounge.com/locations/las/',
  },
  {
    image: 'LHR-centurion-lounge.png',
    imageAlt: 'The Centurion® Lounge, London',
    mobileImage: 'LHR-centurion-lounge-mobile.png',
    mobileImageAlt: 'The Centurion® Lounge, London Mobile',
    cta: 'Explore Lounge',
    url: 'https://www.thecenturionlounge.com/locations/lhr/',
  },
  {
    image: 'DCA-centurion-lounge.jpg',
    imageAlt: 'The Centurion® Lounge, Virginia',
    mobileImage: 'DCA-centurion-lounge-mobile.jpg',
    mobileImageAlt: 'The Centurion® Lounge, Virginia Mobile',
    cta: 'Explore Lounge',
    url: 'https://www.thecenturionlounge.com/locations/dca/',
  },
];

export const CenturionCarrouselSection = () => {
  const baseOneCmsImgUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  const cmsCarouselImages =
    useCenturionNetworkCarouselImages() as CenturionCarouselImage[];
  const { customTrack } = useAnalyticsContext();

  const trackCarouselCtaButton = (
    event: React.MouseEvent<HTMLElement>,
    loungeName: string,
    position: number
  ) => {
    customTrack({
      event,
      element: 'carousel',
      detail: `${loungeName}>>position ${position}>>Explore Lounge`,
    });
  };

  const carouselImages =
    cmsCarouselImages.length > 0 ? cmsCarouselImages : fallbackCarouselImages;

  const carouselArrowControls = 'centurion-homepage-carousel-carousel-items';

  const handleCenturionCarouselArrowClick = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    event.stopPropagation();

    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button');
    if (!(button instanceof HTMLButtonElement)) return;

    const ariaControls = button.getAttribute('aria-controls');
    const controlText = button.textContent?.toLowerCase() || '';

    if (ariaControls === carouselArrowControls) {
      customTrack({
        event,
        element: 'carousel>>arrow',
      });
      return;
    }

    if (controlText.includes('pause automatic slide show')) {
      customTrack({
        event,
        element: 'carousel>>pause',
      });
      return;
    }

    if (controlText.includes('play automatic slide show')) {
      customTrack({
        event,
        element: 'carousel>>play',
      });
    }
  };
  const analyticsDisabled = { disable: true };
  return (
    <div className="tw-pt-10">
      <ExtendReactlytics analytics={analyticsDisabled}>
        <Carousel
          id="centurion-homepage-carousel"
          onClick={handleCenturionCarouselArrowClick}
          shouldAutoPlay={true}
          isSlideFullWidth={true}
        >
          {carouselImages.map(
            ({ image, imageAlt, mobileImage, url, cta }, i) => {
              const imgUrl = `${baseOneCmsImgUrl}${image?.replace(/^\//, '')}`;
              const mobileImgUrl = mobileImage
                ? `${baseOneCmsImgUrl}${mobileImage?.replace(/^\//, '')}`
                : imgUrl;
              const loungeName =
                imageAlt ||
                `#${i + 1} of ${carouselImages.length} images of Centurion Lounge`;

              return (
                <CarouselItem
                  key={imgUrl}
                  id={`centurion-homepage-carousel-slide-${i}`}
                  style={{ borderRadius: 0 }}
                >
                  <div
                    className={`${styles.carouselFrame} tw-relative tw-overflow-hidden`}
                  >
                    <picture className={styles.carouselMedia}>
                      <source
                        media="(max-width: 567px)"
                        srcSet={mobileImgUrl}
                      />
                      <img
                        src={imgUrl}
                        alt={
                          imageAlt ||
                          `#${i + 1} of ${carouselImages.length} images of Centurion Lounge`
                        }
                        className={`${styles.carouselImage} tw-block tw-h-full tw-w-full tw-object-cover`}
                        style={{ borderRadius: 0 }}
                      />
                    </picture>
                    {/* Blur + gradient layer */}
                    <div
                      className={`${styles.carouselOverlay} tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-z-10`}
                    />
                    {/* Content layer */}
                    <div className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-z-20">
                      <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-[1440px] tw-flex-col tw-justify-center tw-gap-4 tw-px-4 tw-py-6 md_tw-flex-row md_tw-items-center md_tw-justify-around md_tw-gap-144">
                        <p className="tw-min-w-0 tw-whitespace-normal tw-text-center tw-font-sans tw-text-base tw-font-normal tw-text-gray-100 md_tw-whitespace-nowrap md_tw-text-left">
                          {imageAlt}
                        </p>
                        {url ? (
                          <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            tabIndex={-1}
                            className="tw-shrink-0"
                          >
                            <Button
                              onClick={(event) =>
                                trackCarouselCtaButton(event, loungeName, i + 1)
                              }
                            >
                              {cta}
                            </Button>
                          </a>
                        ) : (
                          <Button
                            onClick={(event) => {
                              trackCarouselCtaButton(event, loungeName, i + 1);
                              const searchBar =
                                document.querySelector<HTMLElement>(
                                  '#findLounges'
                                );
                              if (searchBar) searchBar.focus();
                            }}
                          >
                            {cta}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              );
            }
          )}
        </Carousel>
      </ExtendReactlytics>
    </div>
  );
};
